<template>
  <Popover :key="popoverKey" class="group">
    <!-- pb-4.5/-mb-4.5 stretch the button down to the header's bottom edge (the menu row's pb-2 plus the
         header's py-2.5) without moving anything, so the space above the underline is clickable too. -->
    <PopoverButton
      role="button"
      :as="to ? 'a' : 'div'"
      :href="to"
      class="flex gap-1 cursor-pointer text-menu-link font-medium items-center relative pb-4.5 -mb-4.5"
      @mouseenter="openPopover"
      @mousedown="openPopover"
      @mouseleave="closePopover"
      @click="handleClick">
      <span class="flex gap-1 items-center">
        <CountryFlag v-if="region" :code="region" />
        <span class="whitespace-nowrap text-base">{{ $t(label) }} </span>
      </span>
      <span
        v-if="!region"
        class="absolute bottom-0 inset-x-0 h-[5px] bg-current"
        :class="highlight() ? 'opacity-100' : 'opacity-0'" />
    </PopoverButton>
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0">
      <!-- Positioned against the sticky #header, which still holds the top bar when it has slid out
           of view. `100%` is that box's bottom edge in both states; `--header-height` only counts the
           visible part, so it would lift the panel over the menu while the top bar is hidden. -->
      <PopoverPanel
        v-if="isOpen"
        as="div"
        static
        class="absolute z-20 overflow-hidden rounded-b-lg focus:outline-none w-fit"
        style="
          top: 100%;
          box-shadow:
            0px 20px 25px -5px rgba(0, 0, 0, 0.1),
            0px 10px 10px 0px rgba(0, 0, 0, 0.04);
        "
        @mouseenter="openPopover"
        @mouseleave="closePopover">
        <slot></slot>
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import CountryFlag from "@/components/CountryFlag.vue";
import { useTimeoutFn } from "@vueuse/core";

import { useI18n } from "vue-i18n";
const { t } = useI18n();
const router = useRouter();

const { start: startCloseTimer, stop: cancelCloseTimer } = useTimeoutFn(() => {
  isOpen.value = false;
}, 200);

const popoverKey = ref(0);
const route = useRoute();

const isOpen = ref(false);

const openPopover = () => {
  cancelCloseTimer();
  isOpen.value = true;
};

const closePopover = () => {
  startCloseTimer();
};

watch(route, () => {
  popoverKey.value++;
  isOpen.value = false;
});

const props = defineProps(["label", "region", "to"]);

const highlight = () => {
  if (
    (route.query?.sport_type && t(`${route.query.sport_type}`) === `${t(props.label).toLowerCase()}`) ||
    (route.fullPath === "/events?sport_type=motorsport,motogp" && props.label === `${t("Other Motorsports")}`) ||
    (route.fullPath === "/events?sport_type=horseracing" && props.label === `${t("Other events")}`) ||
    (route.fullPath === "/football/tournaments" && props.label === `${t("Football")}`) ||
    (route.fullPath === "/tennis/tournaments" && props.label === `${t("Tennis")}`) ||
    isOpen.value
  ) {
    return "underline";
  }
};

const handleClick = (e) => {
  if (props.to) {
    // Navigate to the page on click
    e.preventDefault();
    router.push(props.to);
    isOpen.value = false;
  }
};
</script>
